import { useCallback, useEffect, useRef, useState } from "react";
import { logWarn } from "@/lib/log";
import { ArrowLeft, Send, Moon, Sun, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAdminApi, errMsg } from "@/lib/site";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18].forEach((t) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.15, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.15);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.16);
    });
  } catch (e) {
    logWarn("Notification sound blocked by the browser", e?.message);
  }
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const Availability = ({ available, onChange }) => (
  <label className={`flex items-center gap-3 rounded-2xl border px-4 py-3 cursor-pointer ${available ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
    {available ? <Sun className="w-4 h-4 text-emerald-600" /> : <Moon className="w-4 h-4 text-amber-600" />}
    <span className="text-sm font-semibold flex-1" data-testid="admin-availability-label">{available ? "Available" : "Away"}</span>
    <Switch checked={available} onCheckedChange={onChange} data-testid="admin-away-mode-switch" />
  </label>
);

const Thread = ({ id, onBack }) => {
  const call = useAdminApi();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = useCallback(() => call("get", `/admin/conversations/${id}`).then(setData).catch((e) => logWarn("Thread refresh failed", e?.message)), [call, id]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const el = end.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [data?.messages?.length]);
  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    const t = text; setText("");
    try { await call("post", `/admin/conversations/${id}/reply`, { text: t }); load(); } catch (err) { toast.error(errMsg(err)); setText(t); }
  };
  if (!data) return <div className="p-6 text-slate-400">Loading…</div>;
  const c = data.conversation;
  return (
    <div className="flex flex-col h-full" data-testid="admin-thread">
      <div className="flex items-center gap-3 border-b px-4 py-3 bg-white">
        <button onClick={onBack} className="lg:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100" data-testid="admin-thread-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0">
          <p className="font-display font-bold truncate" data-testid="admin-thread-name">{c.name}</p>
          <div className="flex flex-wrap gap-x-3 text-xs text-slate-500">
            <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-blue-600"><Mail className="w-3 h-3" />{c.email}</a>
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-blue-600"><Phone className="w-3 h-3" />{c.phone}</a>}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-slate-50">
        {data.messages.map((m) => m.sender === "system" ? (
          <p key={m.id} className="text-center text-xs text-amber-700">Away message shown to visitor</p>
        ) : (
          <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.sender === "admin" ? "bg-blue-600 text-white rounded-br-md" : "bg-white border rounded-bl-md"}`}>
              {m.text}
              <p className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-blue-200" : "text-slate-400"}`}>{fmt(m.created_at)}{m.via === "telegram" ? " · via Telegram" : ""}</p>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t bg-white">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" className="flex-1 rounded-xl bg-slate-50 border px-3.5 py-3 text-sm outline-none focus:border-blue-500" data-testid="admin-reply-input" />
        <button className="grid place-items-center w-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-reply-send-btn" aria-label="Send"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

const ConversationItem = ({ c, active, onSelect }) => (
  <button onClick={() => onSelect(c.id)} data-testid={`admin-inbox-item-${c.id}`}
    className={`w-full text-left px-4 py-3.5 border-b flex gap-3 transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}>
    <span className="grid place-items-center w-10 h-10 shrink-0 rounded-full bg-slate-900 text-white font-semibold text-sm">{c.name[0]?.toUpperCase()}</span>
    <span className="min-w-0 flex-1">
      <span className="flex justify-between gap-2"><span className="font-semibold text-sm truncate">{c.name}</span><span className="text-[11px] text-slate-400 shrink-0">{fmt(c.last_at)}</span></span>
      <span className="flex justify-between gap-2 mt-0.5">
        <span className="text-xs text-slate-500 truncate">{c.last_sender === "admin" ? "You: " : ""}{c.last_message}</span>
        {c.unread > 0 && <span className="shrink-0 rounded-full bg-[#FF5733] text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center" data-testid={`admin-inbox-unread-${c.id}`}>{c.unread}</span>}
      </span>
    </span>
  </button>
);

const useInbox = (call) => {
  const [convs, setConvs] = useState([]);
  const unreadRef = useRef(null);
  const load = useCallback(() => call("get", "/admin/conversations").then((list) => {
    const total = list.reduce((a, c) => a + (c.unread || 0), 0);
    if (unreadRef.current !== null && total > unreadRef.current) beep();
    unreadRef.current = total; setConvs(list);
  }).catch((e) => logWarn("Inbox refresh failed", e?.message)), [call]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  return convs;
};

export default function AdminChats() {
  const call = useAdminApi();
  const convs = useInbox(call);
  const [available, setAvailable] = useState(true);
  const [sel, setSel] = useState(null);
  useEffect(() => { call("get", "/admin/settings").then((s) => setAvailable(s.available)).catch((e) => toast.error(errMsg(e))); }, [call]);
  const toggle = async (v) => {
    setAvailable(v);
    try { await call("patch", "/admin/settings", { available: v }); toast.success(v ? "You're available" : "Away mode on"); } catch (e) { setAvailable(!v); toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-chats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Chats</h1>
        <Availability available={available} onChange={toggle} />
      </div>
      <div className="grid lg:grid-cols-[320px_1fr] rounded-3xl border bg-white overflow-hidden h-[calc(100dvh-280px)] lg:h-[calc(100vh-170px)] min-h-[420px]">
        <div className={`border-r overflow-y-auto ${sel ? "hidden lg:block" : ""}`} data-testid="admin-inbox-list">
          {!convs.length && <p className="p-6 text-sm text-slate-500" data-testid="admin-inbox-empty">No chats yet. New conversations from the website appear here.</p>}
          {convs.map((c) => <ConversationItem key={c.id} c={c} active={sel === c.id} onSelect={setSel} />)}
        </div>
        <div className={`${sel ? "" : "hidden lg:grid place-items-center"} min-h-0`}>
          {sel ? <Thread key={sel} id={sel} onBack={() => setSel(null)} /> : <p className="text-sm text-slate-400">Select a conversation</p>}
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-400">Keep this page open to hear a sound when a new message arrives.</p>
    </div>
  );
}
